import { Money } from '../../components/ui/Money';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Calendar, ArrowRightLeft, Info } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  'Assigned': { color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Returned': { color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'Lost': { color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  'Damaged': { color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
};

const PortalAssets = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/my-assets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAssets(res.data.assets || []);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '--';
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  const assigned = assets.filter(a => a.status === 'Assigned');
  const returned = assets.filter(a => a.status === 'Returned');

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-assets">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900" data-testid="assigned-assets-title">Assigned Assets</h2>
        <Badge variant="outline" className="text-xs">{assets.length} item{assets.length !== 1 ? 's' : ''}</Badge>
      </div>

      {/* Summary stats */}
      {assets.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-lg font-bold text-emerald-600">{assigned.length}</p>
                <p className="text-[10px] text-gray-400">Assigned</p>
              </div>
              <div>
                <p className="text-lg font-bold text-blue-600">{returned.length}</p>
                <p className="text-[10px] text-gray-400">Returned</p>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800"><Money value={assigned.reduce((s, a) => s + (a.cost || 0), 0)} /></p>
                <p className="text-[10px] text-gray-400">Total Value (Assigned)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {assets.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No assets assigned to you</p>
          </CardContent>
        </Card>
      ) : (
        assets.map((asset) => {
          const statusCfg = STATUS_CONFIG[asset.status] || STATUS_CONFIG['Assigned'];
          return (
            <Card key={asset.id} className="border-0 shadow-sm" data-testid={`asset-card-${asset.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-sky-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{asset.product_name}</p>
                      <Badge className={`text-[10px] flex-shrink-0 ${statusCfg.color}`}>{asset.status}</Badge>
                    </div>
                    {asset.serial_number && (
                      <p className="text-[11px] text-gray-400 mt-0.5">S/N: {asset.serial_number}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      <span className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Calendar className="h-3 w-3" /> Given: {formatDate(asset.date_given)}
                      </span>
                      {asset.date_returned && (
                        <span className="flex items-center gap-1 text-[11px] text-gray-400">
                          <ArrowRightLeft className="h-3 w-3" /> Returned: {formatDate(asset.date_returned)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {asset.condition_given && (
                        <Badge variant="outline" className="text-[10px]">Given: {asset.condition_given}</Badge>
                      )}
                      {asset.condition_returned && (
                        <Badge variant="outline" className="text-[10px]">Returned: {asset.condition_returned}</Badge>
                      )}
                      {asset.cost > 0 && (
                        <span className="text-[10px] text-gray-400 ml-auto"><Money value={asset.cost} /></span>
                      )}
                    </div>
                    {asset.notes && (
                      <p className="text-[10px] text-gray-400 mt-1.5 flex items-start gap-1">
                        <Info className="h-3 w-3 flex-shrink-0 mt-0.5" /> {asset.notes}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default PortalAssets;
